import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface TestCaseRow {
  readonly testCaseId: string;
  readonly caseCode: string;
  readonly title: string;
  readonly testType: string;
  readonly priority: string;
  readonly status: string;
  readonly versionNo: number;
}

const STATUSES = ['', 'DRAFT', 'READY', 'APPROVED', 'DEPRECATED'];
const TYPES = ['', 'FUNCTIONAL', 'INTEGRATION', 'SYSTEM', 'REGRESSION', 'UAT', 'SECURITY', 'WORKFLOW', 'API', 'REPORT', 'MOBILE'];
const CREATE_TYPES = TYPES.filter(Boolean);

/**
 * M34 — Test Case Designer (§31 / §9 / §10). Register of version-controlled test
 * cases (T450) with the ten classifications, priority and the
 * draft→ready→approved→deprecated lifecycle. Supports designing a case and
 * driving it — mark ready, approve, reject, deprecate and delete — each wired to
 * the guarded /v1/qms/test-cases write API. Filterable by status and test type.
 */
export function TestCaseDesigner(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<TestCaseRow[]>([]);
  const [status, setStatus] = useState('');
  const [testType, setTestType] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState('FUNCTIONAL');
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (testType) params.set('testType', testType);
    const qs = params.toString();
    apiRequest<{ data: TestCaseRow[] }>(`/v1/qms/test-cases${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, status, testType, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m34.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newTitle.trim()) { setActionError(t('m34.case.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/qms/test-cases`, {
        method: 'POST',
        body: { title: newTitle.trim(), testType: newType },
        token,
      });
      setNotice(t('m34.case.created', { title: newTitle.trim() }));
      setNewTitle('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_TEST_CASE_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: TestCaseRow, path: string, label: string, perm: string, body?: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/test-cases/${r.testCaseId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m34.case.notice', { code: r.caseCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  const remove = async (r: TestCaseRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/qms/test-cases/${r.testCaseId}`, { method: 'DELETE', token });
      setNotice(t('m34.case.notice', { code: r.caseCode, label: t('m34.case.act.deleted') }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'M34_TEST_CASE_MANAGE'));
    }
  };

  return (
    <main aria-label="test-case-designer">
      <h1>{t('m34.case.title')}</h1>

      <section aria-label="create-test-case">
        <h2>{t('m34.case.createHeading')}</h2>
        <form aria-label="test-case-form" onSubmit={(e) => void create(e)}>
          <label>{t('m34.common.title')}<input aria-label="field-case-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} /></label>
          <label>{t('m34.case.type')}
            <select aria-label="field-case-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-case" disabled={busy}>{busy ? t('m34.case.creating') : t('m34.case.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="case-action-error">{actionError}</p>}
        {notice && <p aria-label="case-action-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m34.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m34.case.type')}
          <select aria-label="filter-type" value={testType} onChange={(e) => setTestType(e.target.value)}>
            {TYPES.map((s) => <option key={s || 'all'} value={s}>{s || t('m34.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m34.common.loading')}</p> : (
        <>
          <p aria-label="case-count">{t('m34.case.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="case-table"
            rows={rows}
            rowKey={(r) => r.testCaseId}
            columns={[
              { key: 'caseCode', header: t('m34.common.code'), render: (r) => r.caseCode },
              { key: 'title', header: t('m34.common.title'), render: (r) => r.title },
              { key: 'testType', header: t('m34.case.type'), render: (r) => r.testType },
              { key: 'priority', header: t('m34.common.priority'), render: (r) => r.priority },
              { key: 'status', header: t('m34.common.status'), render: (r) => r.status },
              { key: 'versionNo', header: t('m34.case.colVer'), render: (r) => r.versionNo },
              { key: 'actions', header: t('m34.common.actions'), render: (r) => (
                <>
                  {r.status === 'DRAFT' && (
                    <Button variant="secondary" type="button" aria-label={`ready-${r.caseCode}`} onClick={() => void act(r, 'ready', t('m34.case.act.markedReady'), 'M34_TEST_CASE_MANAGE')}>{t('m34.case.btnReady')}</Button>
                  )}
                  {r.status === 'READY' && (
                    <>
                      <Button variant="secondary" type="button" aria-label={`approve-${r.caseCode}`} onClick={() => void act(r, 'approve', t('m34.case.act.approved'), 'M34_TEST_CASE_APPROVE')}>{t('m34.case.btnApprove')}</Button>
                      <input aria-label={`reason-${r.caseCode}`} value={reasons[r.testCaseId] ?? ''} onChange={(e) => setReasons((m) => ({ ...m, [r.testCaseId]: e.target.value }))} placeholder={t('m34.case.rejectReason')} />
                      <Button variant="danger" type="button" aria-label={`reject-${r.caseCode}`} onClick={() => void act(r, 'reject', t('m34.case.act.rejected'), 'M34_TEST_CASE_APPROVE', { reason: reasons[r.testCaseId] ?? '' })}>{t('m34.case.btnReject')}</Button>
                    </>
                  )}
                  {r.status === 'APPROVED' && (
                    <Button variant="secondary" type="button" aria-label={`deprecate-${r.caseCode}`} onClick={() => void act(r, 'deprecate', t('m34.case.act.deprecated'), 'M34_TEST_CASE_MANAGE')}>{t('m34.case.btnDeprecate')}</Button>
                  )}
                  {r.status !== 'DEPRECATED' && (
                    <Button variant="danger" type="button" aria-label={`delete-${r.caseCode}`} onClick={() => void remove(r)}>{t('m34.common.delete')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
